import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { formatMoney } from '../utils/formatMoney';

const Transfer = () => {
  const [fromFileNo, setFromFileNo] = useState('');
  const [toFileNo, setToFileNo] = useState('');
  const [amount, setAmount] = useState('');
  const [remarks, setRemarks] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [availableClients, setAvailableClients] = useState([]);
  const [fromSearchQuery, setFromSearchQuery] = useState('');
  const [toSearchQuery, setToSearchQuery] = useState('');
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target)) {
        setShowFromDropdown(false);
      }
      if (toDropdownRef.current && !toDropdownRef.current.contains(event.target)) {
        setShowToDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch available clients (fileNos) for dropdown
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/ledger/accounts`);
        const fileNos = response.data.accounts.map(acc => acc.fileNo).sort();
        setAvailableClients(fileNos);
      } catch (err) {
        console.error('Failed to fetch clients:', err);
      }
    };
    fetchClients();
  }, []);

  const handleTransfer = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!fromFileNo || !toFileNo || !amount || parseFloat(amount) <= 0) {
      setError('Please fill in all fields with valid values');
      setLoading(false);
      return;
    }

    if (fromFileNo === toFileNo) {
      setError('Source and destination clients must be different');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/ledger/transfer`, {
        fromFileNo,
        toFileNo,
        amount: parseFloat(amount),
        remarks: remarks || undefined,
        transactionDate: transactionDate || undefined,
      });

      setSuccess(
        `Transfer completed successfully! ${formatMoney(amount)} transferred from ${fromFileNo} to ${toFileNo}`
      );

      // Reset form
      setFromFileNo('');
      setToFileNo('');
      setFromSearchQuery('');
      setToSearchQuery('');
      setAmount('');
      setRemarks('');
      setTransactionDate(new Date().toISOString().split('T')[0]);
      setShowFromDropdown(false);
      setShowToDropdown(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete transfer');
    } finally {
      setLoading(false);
    }
  };

  const filteredFromClients = availableClients.filter((client) =>
    client.toLowerCase().includes(fromSearchQuery.toLowerCase())
  );

  const filteredToClients = availableClients.filter((client) =>
    client.toLowerCase().includes(toSearchQuery.toLowerCase())
  );

  const handleFromSelect = (client) => {
    setFromFileNo(client);
    setFromSearchQuery(client);
    setShowFromDropdown(false);
  };

  const handleToSelect = (client) => {
    setToFileNo(client);
    setToSearchQuery(client);
    setShowToDropdown(false);
  };

  return (
    <div>
      <div className="card">
        <h2 style={{ marginBottom: '20px' }}>Transfer Between Client Ledgers</h2>
        <p className="text-muted" style={{ marginBottom: '20px', fontSize: '14px' }}>
          Transfer funds from one client ledger to another. This will create both a transfer_out entry
          for the source client and a transfer_in entry for the destination client.
        </p>

        {error && (
          <div className="alert alert-error">{error}</div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        <form onSubmit={handleTransfer}>
          <div className="form-group">
            <label htmlFor="from-file-no">From Client (File No) *</label>
            <div style={{ position: 'relative' }} ref={fromDropdownRef}>
              <input
                id="from-file-no"
                type="text"
                value={fromSearchQuery}
                onChange={(e) => {
                  setFromSearchQuery(e.target.value);
                  setFromFileNo(e.target.value);
                  setShowFromDropdown(true);
                }}
                onFocus={() => setShowFromDropdown(true)}
                placeholder="Search or enter source client File No..."
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  background: '#fafbfc',
                  color: 'var(--color-neutral-text-main)'
                }}
              />
              {showFromDropdown && availableClients.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                >
                  {filteredFromClients.length > 0 ? (
                    filteredFromClients.map((client) => (
                      <div
                        key={client}
                        onClick={() => handleFromSelect(client)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--color-border)',
                          transition: 'background-color 0.2s',
                          backgroundColor: fromFileNo === client ? '#eff6ff' : 'white'
                        }}
                        onMouseEnter={(e) => {
                          if (fromFileNo !== client) {
                            e.currentTarget.style.backgroundColor = '#f8fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (fromFileNo !== client) {
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        {client}
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '12px 16px', color: 'var(--color-neutral-text-muted)', fontSize: '14px' }}>
                      No clients found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="to-file-no">To Client (File No) *</label>
            <div style={{ position: 'relative' }} ref={toDropdownRef}>
              <input
                id="to-file-no"
                type="text"
                value={toSearchQuery}
                onChange={(e) => {
                  setToSearchQuery(e.target.value);
                  setToFileNo(e.target.value);
                  setShowToDropdown(true);
                }}
                onFocus={() => setShowToDropdown(true)}
                placeholder="Search or enter destination client File No..."
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  background: '#fafbfc',
                  color: 'var(--color-neutral-text-main)'
                }}
              />
              {showToDropdown && availableClients.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                >
                  {filteredToClients.length > 0 ? (
                    filteredToClients.map((client) => (
                      <div
                        key={client}
                        onClick={() => handleToSelect(client)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--color-border)',
                          transition: 'background-color 0.2s',
                          backgroundColor: toFileNo === client ? '#eff6ff' : 'white'
                        }}
                        onMouseEnter={(e) => {
                          if (toFileNo !== client) {
                            e.currentTarget.style.backgroundColor = '#f8fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (toFileNo !== client) {
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        {client}
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '12px 16px', color: 'var(--color-neutral-text-muted)', fontSize: '14px' }}>
                      No clients found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="transfer-amount">Amount *</label>
            <input
              id="transfer-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter transfer amount..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="transfer-remarks">Remarks (Optional)</label>
            <textarea
              id="transfer-remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks or notes for this transfer..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '15px',
                fontFamily: 'inherit',
                resize: 'vertical',
                transition: 'all 0.2s ease',
                background: '#fafbfc',
                color: 'var(--color-neutral-text-main)'
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="transfer-date">Transfer Date *</label>
            <input
              id="transfer-date"
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '15px',
                transition: 'all 0.2s ease',
                background: '#fafbfc',
                color: 'var(--color-neutral-text-main)'
              }}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Execute Transfer'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Transfer;

